// @ts-nocheck
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, incrementProductAmount, decrementProductAmount, cartProducts as cartProductsSelector } from "../stores/cart/cartSlice";
import { selectProductsUsedFallback } from "../stores/menu/productsSlice";
import { FaPlus, FaMinus } from "react-icons/fa";
import { useLanguageContext } from "../context/languageContext";

const ImageReferenceDisclaimer = ({ menuCategoryId }) => {
    const { t } = useLanguageContext();
    const [showTooltip, setShowTooltip] = useState(false);
    const shouldShow = [45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55].includes(menuCategoryId);

    if (!shouldShow) return null;

    return (
        <div className="absolute bottom-2 left-2 z-10">
            <button
                onClick={(e) => { e.stopPropagation(); setShowTooltip(!showTooltip); }}
                className="flex items-center gap-1.5 bg-black/80 backdrop-blur-md px-2 py-1 rounded-md border border-white/10"
            >
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400"></div>
                <span className="text-[10px] font-black uppercase tracking-wider text-white/70 italic">Reference</span>
            </button>
            {showTooltip && (
                <div className="absolute bottom-full mb-2 left-0 w-40 p-2 bg-white rounded-xl shadow-2xl border border-slate-100 text-[11px] font-medium text-slate-500 leading-tight">
                    {t("actual_product_may_vary", "Actual product may vary in appearance.")}
                </div>
            )}
        </div>
    );
};

const ProductDetailCard = ({ product }) => {
    const dispatch = useDispatch();
    const cartProducts = useSelector(cartProductsSelector);
    const cartProduct = cartProducts.find((p) => p.id === product.id);
    const productAmount = cartProduct ? cartProduct.amount : 0;
    const { language, t } = useLanguageContext();
    // Menu served from the bundled copy: the API is down, so an order placed here
    // would have nowhere to go. The card becomes a plain menu entry.
    const offline = useSelector(selectProductsUsedFallback);

    const getTranslatedName = () => {
        if (!product?.itemTranslation) return product.name;
        const translations = product.itemTranslation;
        const map = { en: 'englishName', it: 'italianName', sq: 'albanianName', de: 'germanName', es: 'spanishName', pl: 'polishName', ukr: 'ukrainianName' };
        return translations[map[language]] || product.name;
    };

    return (
        <div className="group flex flex-col bg-white rounded-2xl border border-slate-50 transition-all duration-300 hover:border-indigo-600/10 hover:shadow-[0_15px_30px_-10px_rgba(0,0,0,0.05)] overflow-hidden h-full">
            {/* Architectural Image Container */}
            <div className="relative w-full aspect-square bg-slate-50 overflow-hidden">
                <img
                    src={product.imageUrl}
                    alt={getTranslatedName()}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Price Pill: Sharp Styled */}
                <div className="absolute top-2.5 left-2.5 px-2 py-1 bg-white/95 backdrop-blur-md border border-black/[0.03] rounded-lg shadow-sm">
                    <p className="text-xs font-black text-slate-900 tracking-tight">{product.price} <span className="text-[9px] text-slate-400 uppercase">ALL</span></p>
                </div>

                <ImageReferenceDisclaimer menuCategoryId={product.menuCategoryId} />
            </div>

            {/* Content: Space-Wise Precision */}
            <div className="p-3 flex flex-col flex-grow gap-3">
                <div className="flex-grow space-y-1">
                    <h2 className="text-m font-bold text-slate-900 leading-tight truncate">{getTranslatedName()}</h2>
                    {/* <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{product.euroPrice} €</div> */}
                </div>

                {/* High-Contrast Control Shell - absent entirely while offline, so the
                    card ends on the name and price like a printed menu line. */}
                {!offline && (
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-50">
                    {productAmount > 0 ? (
                        <div className="flex items-center w-full bg-slate-900 rounded-xl p-0.5 shadow-lg animate-fade-in">
                            <button
                                onClick={() => dispatch(decrementProductAmount(product))}
                                className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                            >
                                <FaMinus size={8} />
                            </button>
                            <span className="flex-1 text-center text-xs font-black text-white">{productAmount}</span>
                            <button
                                onClick={() => dispatch(incrementProductAmount(product))}
                                className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                            >
                                <FaPlus size={8} />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => dispatch(addToCart(product))}
                            className="w-full h-9 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 text-slate-900 font-black text-xs uppercase tracking-[0.1em] hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all active:scale-[0.98]"
                        >
                            {/* Simple, Professional CTA */}
                            {t("Add Item")}
                        </button>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default ProductDetailCard;